// @ts-nocheck
import { useCallback, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import {
  startOfWeek,
  endOfWeek,
  isSameDay,
  isSameMonth,
  format,
  subWeeks,
  addWeeks,
  isThisWeek,
  isAfter,
} from 'date-fns';
import { Box, IconButton, Typography } from '@mui/material';
import { ClickAwayListener } from '@mui/base';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

import { FORTA_BLACK, FORTA_BLUE, FORTA_WHITE } from '../../utils/colors';

import type { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import type { StaticDatePickerProps } from '@mui/x-date-pickers/StaticDatePicker';

interface FHWeekDatesPickerProps {
  handlerWidth?: number | string;
  showMobileView?: any;
  textStyles?: any;
  containerStyles?: any;
  dateRangeColor?: string;
  calendarPickerIconButtonStyle?: object;
  calendarPickerIconStyle?: object;
  disableNavigation?: boolean;
  numberOfPastWeeksToDisable?: number;
  value: Date;
  disableDatesFrom?: Date;
  onChange: (date: Date, startDate: Date, endDate: Date) => void;
}

type CustomPickerDayProps = PickersDayProps<Date> & {
  isFirstDay: boolean;
  isLastDay: boolean;
};
const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'isFirstDay' && prop !== 'isLastDay',
})<CustomPickerDayProps>(({ isFirstDay, isLastDay }) => ({
  ...((isFirstDay || isLastDay) && {
    backgroundColor: FORTA_BLUE.primary,
    color: `${FORTA_WHITE} !important`,
    fontSize: '0.85rem',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: FORTA_BLUE.primary,
      color: FORTA_WHITE,
    },
  }),
}));

const arrowIconButtonStyle = (disabled: boolean) => ({
  width: 40,
  height: 40,
  backgroundColor: FORTA_WHITE,
  border: `2px solid ${disabled ? FORTA_BLUE.light3 : FORTA_BLUE.primary}`,

  '&:hover': {
    backgroundColor: FORTA_WHITE,
    border: `2px solid ${FORTA_BLUE.primary}`,
  },

  '&:disabled': {
    backgroundColor: FORTA_WHITE,
  },

  '& svg': {
    fill: disabled ? FORTA_BLUE.light3 : FORTA_BLUE.primary,
  },
});

const calendarIconButtonStyle = {
  width: 32,
  height: 32,
  backgroundColor: FORTA_WHITE,

  '&:hover': {
    backgroundColor: FORTA_WHITE,
  },

  '& svg': {
    fill: FORTA_BLUE.primary,
    width: 20,
    height: 23,
  },
};

const StyledStaticDatePicker = styled(StaticDatePicker)<StaticDatePickerProps<Date>>(() => ({
  borderRadius: '16px',
  minWidth: 280,
  '& .MuiDateCalendar-root': {
    width: '100%',
  },
  '& .MuiDayCalendar-weekDayLabel': {
    margin: 0,
  },
  '& .MuiDayCalendar-header': {
    margin: '0 16px',
  },
  '& .MuiDayCalendar-weekContainer': {
    margin: '2px 16px',
  },
  '& .MuiDayCalendar-weekContainer:has(.MuiPickersDay-root.Mui-selected)': {
    backgroundColor: FORTA_BLUE.light3,
    borderRadius: '16px',
    '& .MuiPickersDay-root': {
      color: FORTA_BLACK,
    },
  },
  '& .MuiPickersDay-root.Mui-selected:not(:first-of-type, :last-of-type)': {
    backgroundColor: FORTA_BLUE.light3,
    color: FORTA_BLACK,

    '&:hover': {
      backgroundColor: FORTA_BLUE.light3,
    },
    '&:focus': {
      backgroundColor: FORTA_BLUE.light3,
    },
  },
}));

const FHWeekDatesPicker = (props: FHWeekDatesPickerProps) => {
  const {
    textStyles = {},
    handlerWidth,
    showMobileView = false,
    dateRangeColor = FORTA_WHITE,
    numberOfPastWeeksToDisable = 0,
    containerStyles = {},
    calendarPickerIconButtonStyle = {},
    calendarPickerIconStyle = {},
    disableNavigation = false,
    disableDatesFrom = new Date(),
    value,
    onChange,
  } = props;
  const [month, setMonth] = useState<Date>(value || new Date());
  const [pickerOpen, setPickerOpen] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date>(startOfWeek(value));
  const [endDate, setEndDate] = useState<Date>(endOfWeek(value));

  useEffect(() => {
    setStartDate(startOfWeek(value));
    setEndDate(endOfWeek(value));
  }, [value]);

  const handleChange = (value: Date | null) => {
    if (!value) return;
    const start = startOfWeek(value);
    const end = endOfWeek(value);
    setStartDate(start);
    setEndDate(end);
    onChange(value, start, end);
  };

  const handleSelectPastWeek = useCallback(() => {
    if (disableNavigation) return;
    const lastWeekDate = subWeeks(value, 1);
    const start = startOfWeek(lastWeekDate);
    const end = endOfWeek(lastWeekDate);
    setStartDate(start);
    setEndDate(end);
    onChange(lastWeekDate, start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, disableNavigation]);

  const handleSelectNextWeek = useCallback(() => {
    if (disableNavigation) return;
    const nextWeekDate = addWeeks(value, 1);
    const start = startOfWeek(nextWeekDate);
    const end = endOfWeek(nextWeekDate);
    setStartDate(start);
    setEndDate(end);
    onChange(nextWeekDate, start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, disableNavigation]);

  const disableFutureWeeks = (day: Date) => {
    return isAfter(day, endOfWeek(subWeeks(disableDatesFrom, numberOfPastWeeksToDisable)));
  };

  const RenderWeekPickerDay = (props: PickersDayProps<Date> & { selectedDay?: Date | null }) => {
    const { day, selectedDay, ...other } = props;

    if (!selectedDay) {
      return <PickersDay day={day} {...other} />;
    }

    const start = startOfWeek(selectedDay);
    const end = endOfWeek(selectedDay);

    const isFirstDay = isSameDay(day, start);
    const isLastDay = isSameDay(day, end);

    return (
      <CustomPickersDay
        {...other}
        disableMargin
        outsideCurrentMonth={!isSameMonth(month, day)}
        showDaysOutsideCurrentMonth={true}
        day={day}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
        onDaySelect={(newValue: Date) => handleChange(newValue)}
      />
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: handlerWidth || 'fit-content',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          backgroundColor: FORTA_BLUE.light1,
          minHeight: 60,
          alignItems: 'center',
          borderRadius: 40,
          padding: '10px',
          justifyContent: 'space-between',
          width: handlerWidth || 'fit-content',
          ...containerStyles,
        }}
      >
        <IconButton
          data-testid="previousWeekButton"
          sx={{ ...arrowIconButtonStyle(disableNavigation) }}
          onClick={handleSelectPastWeek}
          disabled={disableNavigation}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
        <Box display={'flex'} mx={3} alignItems={'center'}>
          {showMobileView ? (
            <>
              <Typography
                variant="h4"
                fontWeight={500}
                color={dateRangeColor}
                mr={1}
                sx={{ ...textStyles }}
              >
                {`${format(startDate, 'MM/dd/yyyy')}`}
              </Typography>
              <Typography
                variant="h4"
                fontWeight={500}
                color={dateRangeColor}
                mr={1}
                sx={{ ...textStyles }}
              >
                {`${format(endDate, 'MM/dd/yyyy')}`}
              </Typography>
            </>
          ) : (
            <Typography
              variant="h4"
              fontWeight={500}
              color={dateRangeColor}
              mr={1}
              sx={{ ...textStyles }}
            >
              {`${format(startDate, 'MMM dd')} - ${format(endDate, 'MMM dd, yyyy')}`}
            </Typography>
          )}
          <IconButton
            sx={{ ...calendarIconButtonStyle, ...calendarPickerIconButtonStyle }}
            onClick={() => setPickerOpen(!pickerOpen)}
          >
            <CalendarTodayOutlinedIcon sx={{ ...calendarPickerIconStyle }} />
          </IconButton>
        </Box>
        <IconButton
          data-testid="nextWeekButton"
          sx={{
            ...arrowIconButtonStyle(
              isThisWeek(addWeeks(value, numberOfPastWeeksToDisable)) || disableNavigation,
            ),
          }}
          disabled={isThisWeek(addWeeks(value, numberOfPastWeeksToDisable)) || disableNavigation}
          onClick={handleSelectNextWeek}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      </Box>
      {pickerOpen && (
        <ClickAwayListener onClickAway={() => setPickerOpen(false)}>
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 50,
              zIndex: 100,
              boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
              borderRadius: '16px',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StyledStaticDatePicker
                displayStaticWrapperAs="desktop"
                slots={{ day: RenderWeekPickerDay }}
                slotProps={{
                  day: {
                    selectedDay: value,
                  },
                }}
                shouldDisableDate={disableFutureWeeks}
                value={value}
                onMonthChange={(month: Date) => setMonth(month)}
                onYearChange={(month: Date) => setMonth(month)}
                onChange={(newValue: Date | null) => handleChange(newValue)}
              />
            </LocalizationProvider>
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default FHWeekDatesPicker;
